/* ==========================================================================
   Mind Body Botanics — theme.js
   Vanilla JS, no dependencies. Covers:
   1. Cart drawer (open/close + AJAX add/change, re-rendered via the
      Section Rendering API so markup stays server-authored)
   2. Product form (purchase-option toggle, variant matching, quantity)
   3. Gallery thumbnail switching
   4. Sticky mobile add-to-cart
   5. Bundle ("Frequently Bought Together") checkbox add
   ========================================================================== */

(function () {
  'use strict';

  /* ------------------------------ Cart drawer ------------------------------ */
  const drawerWrapper = document.querySelector('[data-cart-drawer-wrapper]');

  function openDrawer() {
    if (drawerWrapper) {
      drawerWrapper.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }
  }
  function closeDrawer() {
    if (drawerWrapper) {
      drawerWrapper.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  }

  // Re-fetch the server-rendered cart drawer section and swap it in place.
  async function refreshCartDrawer() {
    const res = await fetch(window.routes.root_url + '?sections=cart-drawer');
    const sections = await res.json();
    const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
    const fresh = html.querySelector('[data-cart-drawer-wrapper]');
    if (fresh && drawerWrapper) {
      const wasOpen = drawerWrapper.classList.contains('is-open');
      drawerWrapper.innerHTML = fresh.innerHTML;
      if (wasOpen) drawerWrapper.classList.add('is-open');
    }
    // Update header count badge
    const cartRes = await fetch(window.routes.cart_url + '.js');
    const cart = await cartRes.json();
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.classList.toggle('is-hidden', cart.item_count === 0);
    });
  }

  async function addToCart(items) {
    const res = await fetch(window.routes.cart_add_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      alert(err.description || 'Could not add to cart.');
      return false;
    }
    await refreshCartDrawer();
    openDrawer();
    return true;
  }

  // Delegated clicks: open / close / qty change work on re-rendered markup too.
  document.addEventListener('click', async (e) => {
    if (e.target.closest('[data-cart-open]')) { e.preventDefault(); openDrawer(); }
    if (e.target.closest('[data-cart-close]')) { e.preventDefault(); closeDrawer(); }

    const qtyBtn = e.target.closest('[data-cart-qty-change]');
    if (qtyBtn) {
      e.preventDefault();
      await fetch(window.routes.cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: Number(qtyBtn.dataset.line), quantity: Number(qtyBtn.dataset.qty) })
      });
      await refreshCartDrawer();
    }
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeDrawer(); });

  /* ------------------------------ Product form ------------------------------ */
  document.querySelectorAll('[data-product-section]').forEach((section) => {
    const form = section.querySelector('[data-product-form]');
    if (!form) return;

    const variantInput = form.querySelector('[data-variant-id]');
    const sellingPlanSelect = form.querySelector('[data-selling-plan]');
    const purchaseOptions = form.querySelectorAll('[data-purchase-option]');
    const variants = JSON.parse(section.querySelector('[data-variant-json]').textContent);
    const moneyFormat = (cents) => {
      // Basic money formatting; matches shop currency via Intl.
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: window.Shopify && Shopify.currency ? Shopify.currency.active : 'USD'
      }).format(cents / 100);
    };

    // Subscribe vs one-time toggle
    purchaseOptions.forEach((opt) => {
      opt.querySelector('input[type="radio"]').addEventListener('change', () => {
        purchaseOptions.forEach((o) => o.classList.remove('is-selected'));
        opt.classList.add('is-selected');
        const isSubscribe = opt.querySelector('input').value === 'subscribe';
        if (sellingPlanSelect) sellingPlanSelect.disabled = !isSubscribe;
      });
    });

    // Variant pill selection → resolve matching variant id + update prices
    const optionFieldsets = section.querySelectorAll('[data-option-index]');
    function currentOptions() {
      return Array.from(optionFieldsets).map(
        (fs) => fs.querySelector('input:checked') && fs.querySelector('input:checked').value
      );
    }
    function updateVariant() {
      const selected = currentOptions();
      const match = variants.find((v) => selected.every((val, i) => v.options[i] === val));
      if (!match) return;
      variantInput.value = match.id;
      const atc = form.querySelector('[data-atc-button]');
      atc.disabled = !match.available;
      atc.textContent = match.available ? 'Add to Cart' : 'Sold Out';
      const onetime = section.querySelector('[data-onetime-price]');
      if (onetime) onetime.textContent = moneyFormat(match.price);
      const compare = section.querySelector('[data-compare-price]');
      if (compare) compare.textContent = moneyFormat(match.price);
      const stickyPrice = section.querySelector('[data-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = moneyFormat(match.price);
      // Note: subscription (allocation) price per-variant would need the
      // allocations serialized too; with a single-variant product the
      // server-rendered price stands.
    }
    optionFieldsets.forEach((fs) =>
      fs.querySelectorAll('[data-option-value]').forEach((input) => input.addEventListener('change', updateVariant))
    );

    // Quantity stepper
    const qtyInput = form.querySelector('[data-qty-input]');
    form.querySelector('[data-qty-minus]')?.addEventListener('click', () => {
      qtyInput.value = Math.max(1, Number(qtyInput.value) - 1);
    });
    form.querySelector('[data-qty-plus]')?.addEventListener('click', () => {
      qtyInput.value = Number(qtyInput.value) + 1;
    });

    // --- Bundle tiers + Subscribe/One-Time tabs ---
    // Subscription unit price comes from the subscription app's selling plan
    // allocation (serialized on the form); one-time from the variant price.
    const tiers = form.querySelectorAll('[data-tier]');
    const tabs = form.querySelectorAll('[data-purchase-tab]');
    const planPicker = form.querySelector('[data-plan-picker]');
    const subscribePerks = form.querySelector('[data-subscribe-perks]');
    const unitPrice = Number(form.dataset.unitPrice || 0);
    const unitCompare = Math.max(Number(form.dataset.unitCompare || 0), unitPrice);
    const subUnitPrice = Number(form.dataset.subUnitPrice || 0);

    function subscribeActive() {
      const checked = form.querySelector('input[name="purchase_type"]:checked');
      return !!checked && checked.value === 'subscribe' && subUnitPrice > 0;
    }

    function reprice() {
      const sub = subscribeActive();
      const activeUnit = sub && subUnitPrice ? subUnitPrice : unitPrice;
      if (sellingPlanSelect) sellingPlanSelect.disabled = !sub;
      if (planPicker) planPicker.hidden = !sub;
      if (subscribePerks) subscribePerks.hidden = !sub;
      tabs.forEach((tab) => tab.classList.toggle('is-active', tab.querySelector('input').checked));

      let selected = null;
      tiers.forEach((tier) => {
        const qty = Number(tier.dataset.qty);
        const paid = Number(tier.dataset.paid);
        const price = activeUnit * paid;
        const compare = unitCompare * qty;
        tier.querySelector('[data-tier-price]').textContent = moneyFormat(price);
        const cmpEl = tier.querySelector('[data-tier-compare]');
        if (cmpEl) { cmpEl.hidden = compare <= price; cmpEl.textContent = moneyFormat(compare); }
        const saveEl = tier.querySelector('[data-tier-save]');
        if (saveEl) {
          const pct = compare > price ? Math.round((1 - price / compare) * 100) : 0;
          saveEl.textContent = '';
          saveEl.hidden = true;
        }
        if (tier.querySelector('input[name="tier"]').checked) selected = tier;
      });
      tiers.forEach((t) => t.classList.toggle('is-selected', t === selected));

      // Main price row + sticky bar follow the selected tier
      const paid = selected ? Number(selected.dataset.paid) : 1;
      const qty = selected ? Number(selected.dataset.qty) : 1;
      if (qtyInput) qtyInput.value = qty;
      const rowPrice = activeUnit * paid;
      const rowCompare = unitCompare * qty;
      const nowEl = section.querySelector('[data-price-now]');
      if (nowEl) nowEl.textContent = moneyFormat(rowPrice);
      const wasEl = section.querySelector('[data-price-was]');
      if (wasEl) { wasEl.hidden = rowCompare <= rowPrice; wasEl.textContent = moneyFormat(rowCompare); }
      const saveRowEl = section.querySelector('[data-price-save]');
      if (saveRowEl) {
        const diff = rowCompare - rowPrice;
        saveRowEl.hidden = diff <= 0;
        saveRowEl.textContent = 'Save ' + moneyFormat(diff).replace(/\.00$/, '');
      }
      const stickyPrice = section.querySelector('[data-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = moneyFormat(rowPrice);
    }

    tiers.forEach((tier) =>
      tier.querySelector('input[name="tier"]').addEventListener('change', reprice)
    );
    tabs.forEach((tab) =>
      tab.querySelector('input').addEventListener('change', reprice)
    );
    if (tiers.length || tabs.length) reprice();

    // AJAX submit → drawer (falls through to normal POST if JS fails)
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const item = { id: Number(variantInput.value), quantity: Number(qtyInput.value) };
      if (sellingPlanSelect && !sellingPlanSelect.disabled) {
        item.selling_plan = Number(sellingPlanSelect.value);
      }
      const btn = form.querySelector('[data-atc-button]');
      btn.disabled = true;
      await addToCart([item]);
      btn.disabled = false;
    });

    /* Gallery */
    const gallery = section.querySelector('[data-gallery]');
    if (gallery) {
      gallery.querySelectorAll('[data-thumb]').forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const idx = thumb.dataset.mediaIndex;
          gallery.querySelectorAll('[data-thumb]').forEach((t) => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
          gallery.querySelectorAll('[data-stage-item]').forEach((item) => {
            item.classList.toggle('is-active', item.dataset.mediaIndex === idx);
          });
        });
      });
    }

    /* Sticky mobile ATC: show once the main buy button scrolls out of view */
    const stickyBar = section.querySelector('[data-sticky-atc]');
    const mainAtc = form.querySelector('[data-atc-button]');
    if (stickyBar && mainAtc && 'IntersectionObserver' in window) {
      new IntersectionObserver(
        ([entry]) => { stickyBar.hidden = entry.isIntersecting; },
        { rootMargin: '0px 0px -60px 0px' }
      ).observe(mainAtc);
      stickyBar.querySelector('[data-sticky-add]').addEventListener('click', () => {
        form.requestSubmit();
      });
    }
  });

  /* ------------------------------ Bundle ------------------------------ */
  document.querySelectorAll('[data-bundle]').forEach((bundle) => {
    const checks = bundle.querySelectorAll('[data-bundle-item]');
    const totalEl = bundle.querySelector('[data-bundle-total]');
    const money = (cents) =>
      new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: window.Shopify && Shopify.currency ? Shopify.currency.active : 'USD'
      }).format(cents / 100);

    function updateTotal() {
      let total = 0;
      checks.forEach((c) => { if (c.checked) total += Number(c.dataset.price); });
      totalEl.textContent = money(total);
    }
    checks.forEach((c) => c.addEventListener('change', updateTotal));
    updateTotal();

    bundle.querySelector('[data-bundle-add]').addEventListener('click', async () => {
      const items = Array.from(checks)
        .filter((c) => c.checked)
        .map((c) => ({ id: Number(c.dataset.variantId), quantity: 1 }));
      if (items.length) await addToCart(items);
    });
  });
})();
